import { env } from '@/config/env';
import type { ManifestExportStart, ManifestExportStatus } from '@/types/dataset';
import type { QueuePendingResponse, QueueEvidenceReviewResponse, ConfirmReviewRequest, QueueTechnicalReportStatus, QueueSettings, AccountHoldOverride } from '@/types/queue';
import type {
  OrganizationsCaseRewardsFilter,
  OrganizationsListResponse,
  OrganizationsTestFilter,
  OrganizationsSortBy,
  OrganizationsSortOrder,
} from '@/types/organizations';
import type {
  BnplInvoice,
  BnplFundingBatch,
  BnplFundingBatchSummary,
  BnplRemittanceResult,
} from '@/types/bnpl';
import type { FirmInvitation, SendFirmInvitationResponse } from '@/types/firmInvitations';
import type { LLMUsageRange, LLMUsageGroupBy, LLMUsageResponse } from '@/types/llmUsage';
import type {
  BlindEvaluation,
  BlindEvaluationEntry,
  CreateBlindEvaluationRequest,
} from '@/types/blindEvaluation';
import type {
  CandidateSummary,
  EvaluationCriterion,
  PromptEvaluation,
  PromptEvaluationResponse,
  PromptEvaluationScore,
  PromptEvaluationSession,
  SessionItem,
  TrendPoint,
} from '@/types/promptEvaluation';
import type {
  Dataset,
  DatasetBreakdownResponse,
  PaginatedDatasetResponse,
  Case,
  Image,
  ImageMetadata,
  DatasetFileSegments,
  Prompt,
  PromptVariableGroup,
  EvaluationRun,
  CreatePromptRequest,
  UpdatePromptRequest,
  CreateEvaluationRunRequest,
  UpdateEvaluationRunRequest,
  QuestionBank,
  QuestionBankListResponse,
  QuestionBankFilters,
  CreateQuestionBankRequest,
  UpdateQuestionBankRequest,
  QuestionBankFilterOptions,
  ResearchPaper,
  ResearchPaperListResponse,
  ResearchPaperFilters,
  ResearchPaperUploadResponse,
  MLTrainingRun,
  MLTrainingRunRequest,
  MLTrainingRunListResponse,
  MLInferenceRequest,
  MLInferenceResponse,
  MLInferenceStatus,
  DeltaVInferenceResult,
  CombinedInferenceResponse,
  EnabledProvidersResponse,
  EDRTargets,
  DatasetCaseSortKey,
  CaseReviewProgress,
  GroundTruthData,
  SimilarCase,
  CompareSimilarRequest,
  CompareSimilarResponse,
  VehicleTypeOption,
  SegmentationUploadResponse,
  SegmentationRunRequest,
  SegmentationRunResponse,
  SegmentationStatusResponse,
  ImageQualityUploadResponse,
  ImageQualityRunRequest,
  ImageQualityRunResponse,
  ImageQualityStatusResponse,
  PDOFToolUploadResponse,
  PDOFToolRunRequest,
  PDOFToolRunResponse,
  PDOFToolStatusResponse,
  CrashSeverityToolResult,
  DeltaVToolUploadResponse,
  DeltaVToolRunRequest,
  DeltaVToolRunResponse,
  DeltaVToolStatusResponse,
  MLLocalModelsResponse,
  HybridInferenceUploadResponse,
  HybridInferenceRunRequest,
  HybridInferenceRunResponse,
  HybridInferenceStatusResponse,
  PDOFEvaluationRun,
  CreatePDOFEvaluationRunRequest,
  PDOFCaseResultsResponse,
  IntakeClassificationUploadResponse,
  IntakeClassificationTempFile,
  IntakeClassificationRunResponse,
  IntakeClassificationStatusResponse,
  IntakeOccupantInput
} from '@/types';

// Environment to source datasets from. "local" = the backend this app is
// configured to talk to (env.API_BASE_URL); "staging"/"production" point at
// the known Silent Witness API hosts so a model can be trained from a dataset
// list browsed in another environment.
export type DatasetSourceEnvironment = 'local' | 'staging' | 'production';

const DATASET_SOURCE_BASE_URLS: Record<Exclude<DatasetSourceEnvironment, 'local'>, string> = {
  staging: 'https://api.staging.silentwitness.ai/apiv2',
  production: 'https://api.silentwitness.ai/apiv2',
};

// Which environment this build is configured to talk to, inferred from its base
// URL (the frontend can't read the server-side APP_ENV). A deployed staging or
// production app matches a known host; anything else is local development.
// Staging is checked first because its host contains the production host as a
// substring after the ".staging" segment is removed.
export function getCurrentDatasetEnvironment(): DatasetSourceEnvironment {
  const baseUrl = env.get('API_BASE_URL');
  if (baseUrl.startsWith(DATASET_SOURCE_BASE_URLS.staging)) return 'staging';
  if (baseUrl.startsWith(DATASET_SOURCE_BASE_URLS.production)) return 'production';
  return 'local';
}

// API Client specific types
interface User {
  id: string;
  email: string;
  name?: string;
}

interface ErrorDetails {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  timestamp?: string;
}

// IIHS Clearance types
export interface ClearanceRange {
  min: number;
  max: number;
}

// IIHSClearanceValues mirrors the Go response: every field is optional because
// the API only emits values that are actually present in the matched IIHS
// record. A nil/undefined field means "not measured", NOT "zero".
export interface IIHSClearanceValues {
  head_to_head_restraint?: ClearanceRange;
  head_to_steering_wheel?: ClearanceRange;
  head_to_dashboard?: ClearanceRange;
  chest_to_dashboard?: ClearanceRange;
  knee_to_dashboard?: ClearanceRange;
  pelvis_angle?: number;
  seat_angle?: number;
}

// data_source: "missing" indicates no IIHS exact or similar match was found.
// In that case `clearances` is null and consumers must surface "no data" to
// the user rather than rendering substituted/fabricated values.
export interface IIHSClearanceResponse {
  success: boolean;
  data_source: 'iihs_exact' | 'iihs_similar' | 'missing';
  matched_vehicle?: string;
  source_type?: 'driver' | 'passenger' | 'unknown';
  clearances: IIHSClearanceValues | null;
  message?: string;
}

// Referral program (staff review queue). Amounts are cents.
export type ReferralStatus =
  | 'pending_review'
  | 'rejected'
  | 'review_expired'
  | 'ready'
  | 'unclaimed'
  | 'claimed'
  | 'issued';

export interface StaffReferral {
  id: string;
  // 'case': the thank-you gift card for a case submitted through the intake
  // (no review; offered on the success screen, sent by staff after Claim).
  kind: 'intro' | 'signup' | 'case';
  // 'manual': a signup staff credited by hand (added_by says who).
  source: 'form' | 'link' | 'linkedin' | 'instagram' | 'intake' | 'manual';
  added_by: string | null;
  case_id: string | null;
  case_name: string | null;
  referrer_user_id: string;
  referrer_name: string;
  referrer_email: string;
  referrer_firm: string;
  referred_firm: string;
  referee_name: string | null;
  referee_email: string | null;
  referee_phone: string | null;
  referee_firm: string | null;
  note: string | null;
  amount_cents: number;
  status: ReferralStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  claim_expires_at: string | null;
  claimed_at: string | null;
  // The card staff sent ('issued': on approval for a referral, from To send
  // for a case): when, by whom, the store, and the last characters of its code
  // ("link" for a link). The code itself never comes back to the dashboard.
  issued_at: string | null;
  sent_by: string | null;
  card_store: string | null;
  card_code_end: string;
  // When the email carrying the card went out; failure_reason says why it
  // could not (then Resend email).
  card_email_sent_at: string | null;
  failure_reason: string | null;
  created_at: string;
  flags: string[];
}

export interface ReferralSettings {
  enabled: boolean;
  intro_reward_cents: number;
  signup_reward_cents: number;
  review_reminder_hours: number;
  // The LinkedIn post and Instagram image on the firm's "Share your link"
  // (app_settings referral_social_share_enabled); off: the link alone.
  social_share_enabled: boolean;
}

// Case reward settings (a gift card per case submitted through the intake).
// Each organization can be switched off or given its own amount on the
// Organizations page.
export interface CaseRewardSettings {
  enabled: boolean;
  default_cents: number;
  roles: ('owner' | 'admin' | 'member')[];
  max_per_user_month: number;
  claim_window_days: number;
  nudge_hours: number;
}

export interface ReferralOverview {
  counts: {
    pending_review: number;
    oldest_pending_at: string | null;
    ready_cents: number;
    // Claimed and waiting for staff to buy and send the card.
    to_send: number;
    to_send_cents: number;
    oldest_to_send_at: string | null;
    issued_this_month_cents: number;
  };
  settings: ReferralSettings;
  case_rewards: CaseRewardSettings;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface RequestConfig extends RequestInit {
  params?: Record<string, string>;
}

// Interfaces for API requests
export interface CreateDatasetRequest {
  name: string;
  description?: string;
  is_evaluation?: boolean;
  is_edr?: boolean;
  is_winsmash?: boolean;
  is_training?: boolean;
  category?: string | null; // Sidebar folder; trimmed server-side, blank or null = "Other"
  dataset_type?: 'training' | 'testing';
  filename?: string;
  file_type?: string;
  file_path?: string;
}

export interface UpdateDatasetRequest {
  name?: string;
  description?: string;
  is_evaluation?: boolean;
  is_edr?: boolean;
  is_winsmash?: boolean;
  is_training?: boolean;
  category?: string | null; // Sidebar folder; trimmed server-side, blank or null clears it
}

export interface CreateCaseRequest {
  name: string;
  description?: string;
  hasEDR?: boolean;
}

export interface UpdateCaseRequest {
  name?: string;
  description?: string;
  hasEDR?: boolean;
}

export class ApiClient {
  private baseUrl: string;
  private publicBaseUrl: string;
  private getToken?: () => Promise<string>;
  private getStaffToken?: () => Promise<string>;
  private user?: User;

  constructor() {
    this.baseUrl = env.get('API_BASE_URL');
    this.publicBaseUrl = env.get('PUBLIC_API_BASE_URL');
  }

  /**
   * Set the token provider function (works with any auth system)
   */
  setAuthProvider(tokenGetter: () => Promise<string>) {
    this.getToken = tokenGetter;
  }

  /**
   * Set the Auth0 token provider for staff calls to the signed-in API
   * (StaffAuthGate). The API checks the token's email against the staff list.
   */
  setStaffTokenProvider(tokenGetter: (() => Promise<string>) | undefined) {
    this.getStaffToken = tokenGetter;
  }

  /**
   * Set the user for email headers (works with any auth system)
   */
  setUser(user: User) {
    this.user = user;
  }

  /**
   * Core fetch method with automatic auth and error handling
   */
  private async fetch<T = unknown>(url: string, options: RequestConfig = {}): Promise<T> {
    const { params, ...fetchOptions } = options;

    // Build URL with query params if provided
    let finalUrl = url;
    if (params) {
      const searchParams = new URLSearchParams(params);
      finalUrl += `?${searchParams.toString()}`;
    }

    const headers: Record<string, string> = {
      ...fetchOptions.headers as Record<string, string>,
    };

    // Auto-set Content-Type unless sending FormData
    if (!(fetchOptions.body instanceof FormData) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(finalUrl, {
      ...fetchOptions,
      headers,
    });

    return this.handleResponse(response);
  }

  /**
   * Authenticated request (includes auth token and user email)
   */
  private async authFetch<T = unknown>(path: string, options: RequestConfig = {}): Promise<T> {
    const headers: Record<string, string> = {
      ...options.headers as Record<string, string>,
    };

    // Add auth token if available
    if (this.getToken) {
      try {
        const token = await this.getToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.warn('Failed to get access token:', error);
      }
    } else {
      console.warn('authFetch: No getAccessToken function available');
    }

    // Add user email if available
    if (this.user?.email) {
      headers['X-User-Email'] = this.user.email;
    }

    const fullUrl = path.startsWith('http') 
      ? path 
      : `${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;

    return this.fetch(fullUrl, {
      ...options,
      headers,
    });
  }

  /**
   * Staff request to the signed-in API (/api), with the staff member's own
   * Auth0 token. Unlike /apiv2, these routes refuse anyone who is not staff.
   */
  private async staffFetch<T = unknown>(path: string, options: RequestConfig = {}): Promise<T> {
    if (!this.getStaffToken) {
      throw new ApiError(401, 'Sign in with your Silent Witness staff account first.');
    }
    const token = await this.getStaffToken();
    return this.fetch(`${env.get('STAFF_API_BASE_URL')}${path}`, {
      ...options,
      headers: { ...(options.headers as Record<string, string>), Authorization: `Bearer ${token}` },
    });
  }

  /**
   * Public request (no auth required)
   */
  private async publicFetch<T = unknown>(path: string, options: RequestConfig = {}): Promise<T> {
    const fullUrl = path.startsWith('http')
      ? path
      : `${this.publicBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;

    return this.fetch(fullUrl, options);
  }

  /**
   * Public request that returns full response (including pagination, etc.)
   */
  private async publicFetchRaw<T = unknown>(path: string, options: RequestConfig = {}): Promise<T> {
    const fullUrl = path.startsWith('http')
      ? path
      : `${this.publicBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;

    const { params, ...fetchOptions } = options;

    // Build URL with query params if provided
    let finalUrl = fullUrl;
    if (params) {
      const searchParams = new URLSearchParams(params);
      finalUrl += `?${searchParams.toString()}`;
    }

    const headers: Record<string, string> = {
      ...fetchOptions.headers as Record<string, string>,
    };

    // Auto-set Content-Type unless sending FormData
    if (!(fetchOptions.body instanceof FormData) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(finalUrl, {
      ...fetchOptions,
      headers,
    });

    if (!response.ok) {
      let errorMessage = `API Error: ${response.statusText}`;

      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch {
        // If we can't parse the error response, use the default message
      }

      throw new ApiError(response.status, errorMessage);
    }

    // Return full response without extracting data field
    return response.json();
  }

  /**
   * Handle API response with proper error handling
   */
  private async handleResponse<T = unknown>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorMessage = `API Error: ${response.statusText}`;
      
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch {
        // If we can't parse the error response, use the default message
      }
      
      throw new ApiError(response.status, errorMessage);
    }

    // Return null for 204 No Content responses
    if (response.status === 204) {
      return null as T;
    }

    const data = await response.json();
    
    // Handle the backend response format { success: true, data: ... }
    if (data && typeof data === 'object' && 'success' in data && 'data' in data) {
      return data.data;
    }
    
    return data;
  }

  // Review Queue — production cases pending Delta-V sign-off (internal /apiv2).
  queue = {
    listDeltaVReview: (): Promise<QueuePendingResponse> =>
      this.publicFetch('/queue/deltav-review'),

    // Intake cases still in the Evidence Review stage, across all accounts. A
    // lighter payload than the Delta-V queue (no crash results yet) — just the
    // case, its owning account, and the seams to jump in impersonating it.
    listEvidenceReview: (): Promise<QueueEvidenceReviewResponse> =>
      this.publicFetch('/queue/evidence-review'),

    // Hard-delete an intake case that is still in Evidence Review (S3 files,
    // invoice reversal, and DB cascade — same path as the web app's delete).
    // The backend rejects cases that already progressed past intake triage.
    deleteEvidenceReviewCase: (caseId: string): Promise<{ success: boolean }> =>
      this.publicFetch(`/queue/evidence-review/${caseId}`, { method: 'DELETE' }),

    // Finalized cases, read from persisted review_status so the Confirmed /
    // Auto-confirmed tabs survive a reload (they're real server state, not
    // session-only bookkeeping).
    listConfirmed: (): Promise<QueuePendingResponse> =>
      this.publicFetch('/queue/confirmed'),

    listAutoConfirmed: (): Promise<QueuePendingResponse> =>
      this.publicFetch('/queue/auto-confirmed'),

    // Sign off a case (optionally overwriting per-vehicle values), removing it
    // from the pending queue. Empty body confirms the computed values as-is.
    confirm: (caseId: string, body: ConfirmReviewRequest = {}): Promise<{ success: boolean }> =>
      this.publicFetch(`/queue/${caseId}/confirm`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    // Pause / resume the auto-confirm countdown for a case under review.
    hold: (caseId: string): Promise<{ success: boolean }> =>
      this.publicFetch(`/queue/${caseId}/hold`, { method: 'POST' }),

    resume: (caseId: string): Promise<{ success: boolean }> =>
      this.publicFetch(`/queue/${caseId}/resume`, { method: 'POST' }),

    // Kick off the real technical-report workflow for a queued case so the
    // reviewer can preview the document before signing it off. Reuses the
    // case's actual report record (no billing gate on this internal surface).
    generateTechnicalReport: (caseId: string): Promise<QueueTechnicalReportStatus> =>
      this.publicFetch(`/queue/${caseId}/technical-report`, { method: 'POST' }),

    // Poll the case's technical-report progress; returns status "idle" until a
    // report is generated, and the PDF/DOCX URLs once it completes.
    getTechnicalReportStatus: (caseId: string): Promise<QueueTechnicalReportStatus> =>
      this.publicFetch(`/queue/${caseId}/technical-report`),

    // Upload a reviewer-supplied PDF (and optional DOCX) that becomes the case's
    // official technical report — the same files the customer sees on the report
    // link. Returns status "completed" with the uploaded URLs. Coexists with
    // generateTechnicalReport (most-recent action wins).
    uploadTechnicalReport: (
      caseId: string,
      file: File,
      docx?: File
    ): Promise<QueueTechnicalReportStatus> => {
      const form = new FormData();
      form.append('file', file);
      if (docx) form.append('docx', docx);
      return this.publicFetch(`/queue/${caseId}/technical-report/upload`, {
        method: 'POST',
        body: form,
      });
    },

    // Remove the reviewer-uploaded report so the case serves its generated one
    // again. A genuine restore, not a regeneration: the generated report was
    // never touched by the upload, so it returns on the next read and the
    // customer's Report Builder unblocks immediately.
    deleteUploadedTechnicalReport: (caseId: string): Promise<{ success: boolean }> =>
      this.publicFetch(`/queue/${caseId}/technical-report/upload`, { method: 'DELETE' }),

    // Per-instance review-hold setting (the queue's auto-confirm window). The
    // value is a Go duration ("0s","10m","1h") or "forever"; changing it only
    // affects NEW cases entering review.
    getSettings: (): Promise<QueueSettings> =>
      this.publicFetch('/queue/settings'),

    updateSettings: (reviewHoldWindow: string): Promise<QueueSettings> =>
      this.publicFetch('/queue/settings', {
        method: 'PUT',
        body: JSON.stringify({ reviewHoldWindow }),
      }),

    // The Evidence Review target window (informational — drives the "time left"
    // hint). Read fresh on every queue load, so a change applies immediately.
    updateEvidenceReviewWindow: (evidenceReviewWindow: string): Promise<QueueSettings> =>
      this.publicFetch('/queue/evidence-review-window', {
        method: 'PUT',
        body: JSON.stringify({ evidenceReviewWindow }),
      }),
  };

  // Accounts — used by the per-account review-hold override editor on the queue
  // settings page (internal /apiv2). The list is small (few customers) so it's
  // fetched whole and filtered client-side.
  accounts = {
    list: async (): Promise<AccountHoldOverride[]> => {
      const res = await this.publicFetch<{ accounts: AccountHoldOverride[] }>('/accounts');
      return res.accounts ?? [];
    },

    // Set or clear an account's review-hold override. Empty string clears it
    // (the account reverts to the global default).
    setReviewHoldWindow: (
      accountId: string,
      reviewHoldWindow: string,
    ): Promise<{ reviewHoldWindow: string; source: 'account' | 'inherited' }> =>
      this.publicFetch(`/accounts/${accountId}/review-hold-window`, {
        method: 'PUT',
        body: JSON.stringify({ reviewHoldWindow }),
      }),
  };

  // Organizations directory — paginated + searchable account list for the
  // Organizations page (internal /apiv2).
  organizations = {
    list: (
      params: {
        page?: number;
        limit?: number;
        search?: string;
        testFilter?: OrganizationsTestFilter;
        caseRewards?: OrganizationsCaseRewardsFilter;
        sortBy?: OrganizationsSortBy;
        sortOrder?: OrganizationsSortOrder;
      } = {},
    ): Promise<OrganizationsListResponse> => {
      const query: Record<string, string> = {};
      if (params.page) query.page = String(params.page);
      if (params.limit) query.limit = String(params.limit);
      if (params.search) query.search = params.search;
      if (params.testFilter) query.testFilter = params.testFilter;
      if (params.caseRewards && params.caseRewards !== 'all') query.caseRewards = params.caseRewards;
      if (params.sortBy) query.sortBy = params.sortBy;
      if (params.sortOrder) query.sortOrder = params.sortOrder;
      return this.publicFetch('/organizations', { params: query });
    },

    setTestFlag: (accountId: string, isTestAccount: boolean): Promise<{ isTestAccount: boolean }> =>
      this.publicFetch(`/organizations/${accountId}/test-flag`, {
        method: 'PUT',
        body: JSON.stringify({ isTestAccount }),
      }),

    // Sets or clears (null) the account's Pay-Per-Use per-case price override
    // (issue #596). The API validates plan (409) and bounds (400).
    setCasePrice: (
      accountId: string,
      casePriceCents: number | null,
    ): Promise<{
      planType: string;
      casePriceCents: number | null;
      defaultCasePriceCents: number | null;
      customCasePriceCents: number | null;
    }> =>
      this.publicFetch(`/organizations/${accountId}/case-price`, {
        method: 'PUT',
        body: JSON.stringify({ casePriceCents }),
      }),
  };

  // BNPL funding console: export pending local invoices to the provider as
  // batch CSVs and reconcile remittance CSVs back into paid invoices.
  bnpl = {
    listInvoices: (
      filter: 'pending' | 'batched' | 'all',
    ): Promise<{ invoices: BnplInvoice[]; count: number; total_cents: number }> =>
      this.publicFetch(`/bnpl/invoices?filter=${filter}`),

    createBatch: (): Promise<{ batch: BnplFundingBatch; invoices: BnplInvoice[] }> =>
      this.publicFetch('/bnpl/batches', { method: 'POST' }),

    listBatches: (): Promise<{ batches: BnplFundingBatchSummary[] }> =>
      this.publicFetch('/bnpl/batches'),

    getBatchInvoices: (batchId: string): Promise<{ invoices: BnplInvoice[] }> =>
      this.publicFetch(`/bnpl/batches/${batchId}/invoices`),

    uploadRemittance: (file: File, dryRun: boolean): Promise<BnplRemittanceResult> => {
      const formData = new FormData();
      formData.append('file', file);
      return this.publicFetch(`/bnpl/remittance?dry_run=${dryRun}`, {
        method: 'POST',
        body: formData,
      });
    },

    // Direct download URL for a batch CSV (the endpoint streams text/csv).
    batchCsvUrl: (batchId: string): string => `${this.publicBaseUrl}/bnpl/batches/${batchId}/csv`,
  };

  // LLM Spend — rollups over the llm_usage ledger for the Usage page
  // (issue #760). One call returns the whole page: tiles, their comparison
  // window, the sparkline, the area bars and the breakdown rows, all computed
  // over the same window server-side so no two widgets can disagree.
  llmUsage = {
    get: (range: LLMUsageRange, groupBy: LLMUsageGroupBy): Promise<LLMUsageResponse> =>
      this.publicFetch(`/llm-usage?range=${range}&group_by=${groupBy}`),
  };

  // Firm invitations — staff "Invite Firm" leads (issue #534). Same backend
  // service as the SW toolbar's /api/staff/firm-invites; the recipient signs
  // up self-serve and the invitation is matched by email on first login.
  // Referral program review queue. Staff-authenticated (staffFetch): approving
  // a referral lets its referrer claim a gift card, and sending one emails the
  // card staff bought.
  referrals = {
    list: (statuses: ReferralStatus[] = []): Promise<StaffReferral[]> =>
      this.staffFetch('/staff/referrals', statuses.length ? { params: { status: statuses.join(',') } } : {}),

    overview: (): Promise<ReferralOverview> => this.staffFetch('/staff/referrals/overview'),

    // Approves a referral with the gift card staff bought for it (the store
    // and the code, or the link the store gave); the referrer gets it by email.
    approve: (id: string, store: string, code: string): Promise<StaffReferral> =>
      this.staffFetch(`/staff/referrals/${encodeURIComponent(id)}/approve`, {
        method: 'POST',
        body: JSON.stringify({ store, code }),
      }),

    reject: (id: string, note?: string): Promise<StaffReferral> =>
      this.staffFetch(`/staff/referrals/${encodeURIComponent(id)}/reject`, {
        method: 'POST',
        body: JSON.stringify({ note: note ?? '' }),
      }),

    // Sends the gift card staff bought for a claimed case reward: the store and
    // the code (or the link the store gave). The person gets it by email.
    sendCard: (id: string, store: string, code: string): Promise<StaffReferral> =>
      this.staffFetch(`/staff/referrals/${encodeURIComponent(id)}/send-card`, {
        method: 'POST',
        body: JSON.stringify({ store, code }),
      }),

    // Emails a sent card again (a failed or lost email).
    resendCard: (id: string): Promise<StaffReferral> =>
      this.staffFetch(`/staff/referrals/${encodeURIComponent(id)}/resend-card`, { method: 'POST' }),

    // Credits a firm's signup to a referrer by hand (a referral by word of
    // mouth), or moves a signup waiting for review to that referrer. It then
    // waits for review like any other. referredBy: an email or a code.
    creditSignup: (newFirmEmail: string, referredBy: string): Promise<StaffReferral> =>
      this.staffFetch('/staff/referrals/credit-signup', {
        method: 'POST',
        body: JSON.stringify({ new_firm_email: newFirmEmail, referred_by: referredBy }),
      }),

    updateSettings: (settings: ReferralSettings): Promise<ReferralSettings> =>
      this.staffFetch('/staff/referrals/settings', { method: 'PUT', body: JSON.stringify(settings) }),

    updateCaseRewardSettings: (settings: CaseRewardSettings): Promise<CaseRewardSettings> =>
      this.staffFetch('/staff/referrals/case-settings', { method: 'PUT', body: JSON.stringify(settings) }),

    // An organization's case reward switch and amount (null = the default).
    // Staff-authenticated: the switch controls spending, so it never goes
    // through /apiv2 like the page's other actions.
    setOrganizationCaseRewards: (
      accountId: string,
      enabled: boolean,
      amountCents: number | null,
    ): Promise<{ caseRewardsEnabled: boolean; caseRewardCents: number | null }> =>
      this.staffFetch(`/staff/organizations/${encodeURIComponent(accountId)}/case-rewards`, {
        method: 'PUT',
        body: JSON.stringify({ enabled, amountCents }),
      }),
  };

  firmInvitations = {
    list: (): Promise<{ invitations: FirmInvitation[]; count: number }> =>
      this.publicFetch('/firm-invites'),

    send: (payload: {
      firm_name: string;
      contact_name?: string;
      contact_email: string;
    }): Promise<SendFirmInvitationResponse> =>
      this.publicFetch('/firm-invites', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
  };

  // Dataset API methods
  datasets = {
    // Get all datasets from the given environment (defaults to local).
    // publicFetch passes absolute URLs through unchanged, so staging/production
    // hit those hosts directly while local uses the configured base URL.
    getAll: (environment: DatasetSourceEnvironment = 'local'): Promise<Dataset[]> => {
      if (environment === 'local') {
        return this.publicFetch('/datasets');
      }
      return this.publicFetch(`${DATASET_SOURCE_BASE_URLS[environment]}/datasets`);
    },

    // Active-case counts grouped by (delta-v range × vehicle type × PDOF
    // direction) across the given datasets. GET + comma-joined ids so it sources
    // cross-environment exactly like getAll (a JSON POST body would trip a CORS
    // preflight on localhost→prod). The payload is tiny, so the page pivots and
    // filters it entirely client-side.
    getBreakdown: (
      datasetIds: string[],
      environment: DatasetSourceEnvironment = 'local',
    ): Promise<DatasetBreakdownResponse> => {
      const query = { dataset_ids: datasetIds.join(',') };
      if (environment === 'local') {
        return this.publicFetch('/datasets/breakdown', { params: query });
      }
      return this.publicFetch(
        `${DATASET_SOURCE_BASE_URLS[environment]}/datasets/breakdown`,
        { params: query },
      );
    },

    // Like findSimilarCases but without a source case — accepts raw crash params.
    findSimilarByParams: (params: {
      finalMaxDeltaV: number;
      pdofAngle: number; // degrees, [0, 360)
      vehicleType?: string; // empty / undefined = no vehicle-type filter
      limit?: number;
      minSimilarity?: number; // 0.0–1.0, 0 = no filter
      // When both are set, the backend loads that case vehicle's stored
      // damage-profile scores and scores matches on the damage-profile path
      // (real, uncapped) instead of the ΔV/PDOF output-only fallback (capped 0.82).
      caseId?: string;
      vehicleRole?: string; // "Plaintiff" | "Defendant"
    }): Promise<SimilarCase[]> => {
      const query: Record<string, string> = {
        final_max_delta_v: params.finalMaxDeltaV.toString(),
        pdof_angle: params.pdofAngle.toString(),
      };
      if (params.limit !== undefined) {
        query.limit = params.limit.toString();
      }
      if (params.vehicleType) {
        query.vehicle_type = params.vehicleType;
      }
      if ((params.minSimilarity ?? 0) > 0) {
        query.min_similarity = params.minSimilarity!.toFixed(2);
      }
      if (params.caseId && params.vehicleRole) {
        query.case_id = params.caseId;
        query.vehicle_role = params.vehicleRole;
      }
      return this.publicFetch('/datasets/similar', { params: query });
    },

    // Run N parameter sets in parallel against the same base context. Used by
    // the Compare view on /find-similar-cases to tune fetch/rerank settings
    // without hard-coding presets on the backend.
    compareSimilar: (body: CompareSimilarRequest): Promise<CompareSimilarResponse> => {
      return this.publicFetch('/datasets/similar/compare', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    findSimilarByPhotos: (files: File[], params?: {
      limit?: number;
      minSimilarity?: number;
      finalMaxDeltaV?: number;
      pdofAngle?: number;
      vehicleType?: string;
    }): Promise<{
      success: boolean;
      message: string;
      workflowId: string;
      runId: string;
      limit: number;
      minSimilarity: number;
      finalMaxDeltaV?: number;
      pdofAngle?: number;
      vehicleType?: string;
    }> => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      formData.append('min_similarity', String(params?.minSimilarity ?? 0));
      if (params?.limit !== undefined) {
        formData.append('limit', String(params.limit));
      }
      if (params?.finalMaxDeltaV !== undefined) {
        formData.append('final_max_delta_v', String(params.finalMaxDeltaV));
      }
      if (params?.pdofAngle !== undefined) {
        formData.append('pdof_angle', String(params.pdofAngle));
      }
      if (params?.vehicleType) {
        formData.append('vehicle_type', params.vehicleType);
      }

      return this.publicFetch('/datasets/similar/photos', {
        method: 'POST',
        body: formData,
        headers: {},
      });
    },

    // Get all flagged cases across all datasets with optional category filter
    getAllFlaggedCases: (category?: string): Promise<Case[]> => {
      const params: Record<string, string> = {};
      if (category) {
        params.category = category;
      }
      return this.publicFetch('/datasets/flagged-cases/all', { params });
    },

    // ── blind evaluations ────────────────────────────────────────────────
    // A named person judges a curated case list from photos alone; every save
    // appends an entry, so runs can be paused and resumed.
    listBlindEvaluations: (): Promise<{ data: BlindEvaluation[] }> =>
      this.publicFetchRaw('/blind-evaluations'),

    createBlindEvaluation: (payload: CreateBlindEvaluationRequest): Promise<{ data: BlindEvaluation }> =>
      this.publicFetchRaw('/blind-evaluations', { method: 'POST', body: JSON.stringify(payload) }),

    getBlindEvaluation: (id: string): Promise<{ data: BlindEvaluation; entries: BlindEvaluationEntry[] }> =>
      this.publicFetchRaw(`/blind-evaluations/${id}`),

    deleteBlindEvaluation: (id: string): Promise<{ success: boolean }> =>
      this.publicFetchRaw(`/blind-evaluations/${id}`, { method: 'DELETE' }),

    addBlindEvaluationEntry: (
      id: string,
      payload: { dataset_case_id: string; delta_v_mph: number | null; pdof_degrees: number | null; crash_pulse_ms: number | null },
    ): Promise<{ data: BlindEvaluationEntry }> =>
      this.publicFetchRaw(`/blind-evaluations/${id}/entries`, { method: 'POST', body: JSON.stringify(payload) }),

    /** Absolute URL for the CSV download (served with Content-Disposition). */
    blindEvaluationExportUrl: (id: string): string => `${this.baseUrl}/blind-evaluations/${id}/export`,

    // ── prompt evaluations ───────────────────────────────────────────────
    // Candidates answer one prompt on fixed subjects; a person scores every
    // answer without being told who wrote it. `id` takes the UUID or the slug.
    // The pooled results and the trend are computed server-side — two
    // implementations of the same maths would drift.
    listPromptEvaluations: (): Promise<{ data: PromptEvaluation[] }> =>
      this.publicFetchRaw('/prompt-evaluations'),

    getPromptEvaluation: (id: string): Promise<{ data: PromptEvaluation }> =>
      this.publicFetchRaw(`/prompt-evaluations/${id}`),

    listPromptEvaluationResponses: (id: string): Promise<{ data: PromptEvaluationResponse[] }> =>
      this.publicFetchRaw(`/prompt-evaluations/${id}/responses`),

    /** Overwrites the cells it is given, leaving the rest of the corpus alone. */
    loadPromptEvaluationResponses: (
      id: string,
      responses: Array<{ subject_key: string; candidate_key: string; content: { text: string } }>,
    ): Promise<{ loaded: number }> =>
      this.publicFetchRaw(`/prompt-evaluations/${id}/responses`, {
        method: 'PUT',
        body: JSON.stringify({ responses }),
      }),

    /**
     * Generates ONE cell of the corpus by calling that candidate's model.
     * One per request so the caller can show progress and a failure costs one
     * answer rather than the whole grid. Returns no response text — this is
     * called from the list page, which must never hold an answer next to the
     * candidate that wrote it.
     */
    generatePromptEvaluationResponse: (
      id: string,
      payload: { subject_key: string; candidate_key: string; overwrite?: boolean },
    ): Promise<{ generated: boolean; skipped: boolean; chars: number }> =>
      this.publicFetchRaw(`/prompt-evaluations/${id}/generate`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    listPromptEvaluationSessions: (id: string): Promise<{ data: PromptEvaluationSession[] }> =>
      this.publicFetchRaw(`/prompt-evaluations/${id}/sessions`),

    createPromptEvaluationSession: (
      id: string,
      payload: { evaluator_name: string },
    ): Promise<{ data: PromptEvaluationSession }> =>
      this.publicFetchRaw(`/prompt-evaluations/${id}/sessions`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    summarisePromptEvaluation: (id: string): Promise<{ data: CandidateSummary[] }> =>
      this.publicFetchRaw(`/prompt-evaluations/${id}/summary`),

    promptEvaluationTrend: (id: string): Promise<{ data: TrendPoint[] }> =>
      this.publicFetchRaw(`/prompt-evaluations/${id}/trend`),

    /**
     * The pass, the rubric, and the queue rendered blind. Everything the
     * scoring page needs and nothing more: no candidate key reaches the
     * browser, so the run stays blind in the network tab as well as on screen.
     */
    getPromptEvaluationSession: (
      sessionId: string,
    ): Promise<{
      data: PromptEvaluationSession;
      criteria: EvaluationCriterion[];
      items: SessionItem[];
    }> => this.publicFetchRaw(`/prompt-evaluation-sessions/${sessionId}`),

    /**
     * Produces the answer for one queue position, as the evaluator reaches it.
     * By position, never by candidate — the scoring page is not told who wrote
     * what. The answer is written to the shared corpus, so the next evaluator
     * to reach this position scores the same text.
     */
    generateSessionResponseAt: (
      sessionId: string,
      payload: { position: number },
    ): Promise<{ generated: boolean }> =>
      this.publicFetchRaw(`/prompt-evaluation-sessions/${sessionId}/generate`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    /** Scores address a 1-based queue position; the server resolves the pair. */
    addPromptEvaluationScore: (
      sessionId: string,
      payload: { position: number; scores: Record<string, number> },
    ): Promise<{ data: PromptEvaluationScore }> =>
      this.publicFetchRaw(`/prompt-evaluation-sessions/${sessionId}/scores`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    completePromptEvaluationSession: (sessionId: string): Promise<{ success: boolean }> =>
      this.publicFetchRaw(`/prompt-evaluation-sessions/${sessionId}/complete`, { method: 'POST' }),

    deletePromptEvaluationSession: (sessionId: string): Promise<{ success: boolean }> =>
      this.publicFetchRaw(`/prompt-evaluation-sessions/${sessionId}`, { method: 'DELETE' }),

    getAllCases: async (filters?: {
      search?: string;
      vehicleTypes?: string[];
      noVehicleType?: boolean;
      noCurbWeight?: boolean;
      noVehicleYMM?: boolean;
      datasetId?: string;
      datasetIds?: string[];
      hasEDR?: boolean;
      flagged?: boolean;
      notFlagged?: boolean;
      hideConfirmed?: boolean; // Exclude review-confirmed cases (reviewed_at set)
      customList?: string; // Curated worklist key; the API expands it into a case ID list
      flagCategories?: string[];
      deltaVImplausible?: boolean;
      hasDSHuman?: boolean;
      hasDSLLM?: boolean;
      hasEDRHuman?: boolean;
      hasEDRLLM?: boolean;
      pdofDirections?: string[];
      noPDOFDirection?: boolean;
      deltaVBucket?: string; // dataset-breakdown Δ-V band drill-down ("0-5".."25+","unknown")
      pdofBucket?: string;   // dataset-breakdown PDOF direction drill-down ("front"/"side"/"rear"/"unknown")
      pdofDegreesMin?: number;
      pdofDegreesMax?: number;
      peakAccelMin?: number;
      peakAccelMax?: number;
      deltaVAtPeakMin?: number;
      deltaVAtPeakMax?: number;
      finalDeltaVMin?: number;
      finalDeltaVMax?: number;
      crashDurationMin?: number;
      crashDurationMax?: number;
      edrFormats?: string[];
      noEDRFormat?: boolean;
      edrEventCounts?: number[];
      noEDREventCount?: boolean;
      hasDeltaVLongitudinal?: boolean;
      hasDeltaVLateral?: boolean;
      hasAccelLongitudinal?: boolean;
      hasAccelLateral?: boolean;
      imageCounts?: number[];
      imageCountAtLeast?: number;
      imagePerspectives?: string[];
      imageFramings?: string[];
      imageFocusAreas?: string[];
      caseLabels?: string[];
      limit?: number;
      page?: number;
      sortBy?: 'final_delta_v' | 'pdof' | 'pulse_duration' | 'vehicle_type' | 'edr_format' | 'event_count' | 'edr_pages' | 'delta_v_channels' | 'accel_channels' | 'created_at';
      /** Multi-key sort with bucketing (card view); wins over sortBy/sortOrder when present. */
      sort?: DatasetCaseSortKey[];
      sortOrder?: 'asc' | 'desc';
    }): Promise<{ data: Case[]; pagination: { total: number; page: number; limit: number; totalPages: number; hasNextPage: boolean; hasPrevPage: boolean }; review?: CaseReviewProgress }> => {
      // Build JSON payload for POST request
      const payload = {
        search: filters?.search || '',
        vehicle_types: filters?.vehicleTypes || [],
        no_vehicle_type: filters?.noVehicleType || false,
        no_curb_weight: filters?.noCurbWeight || false,
        no_vehicle_ymm: filters?.noVehicleYMM || false,
        dataset_id: filters?.datasetId || '',
        dataset_ids: filters?.datasetIds || [],
        has_edr: filters?.hasEDR,
        flagged: filters?.flagged,
        not_flagged: filters?.notFlagged || false,
        hide_confirmed: filters?.hideConfirmed || false,
        custom_list: filters?.customList || "",
        flag_categories: filters?.flagCategories || [],
        delta_v_implausible: filters?.deltaVImplausible || false,
        has_ds_human: filters?.hasDSHuman,
        has_ds_llm: filters?.hasDSLLM,
        has_edr_human: filters?.hasEDRHuman,
        has_edr_llm: filters?.hasEDRLLM,
        pdof_directions: filters?.pdofDirections || [],
        no_pdof_direction: filters?.noPDOFDirection || false,
        delta_v_bucket: filters?.deltaVBucket || '',
        pdof_bucket: filters?.pdofBucket || '',
        pdof_degrees_min: filters?.pdofDegreesMin,
        pdof_degrees_max: filters?.pdofDegreesMax,
        peak_accel_min: filters?.peakAccelMin,
        peak_accel_max: filters?.peakAccelMax,
        delta_v_at_peak_min: filters?.deltaVAtPeakMin,
        delta_v_at_peak_max: filters?.deltaVAtPeakMax,
        final_delta_v_min: filters?.finalDeltaVMin,
        final_delta_v_max: filters?.finalDeltaVMax,
        crash_duration_min: filters?.crashDurationMin,
        crash_duration_max: filters?.crashDurationMax,
        edr_formats: filters?.edrFormats || [],
        no_edr_format: filters?.noEDRFormat || false,
        edr_event_counts: filters?.edrEventCounts || [],
        no_edr_event_count: filters?.noEDREventCount || false,
        has_delta_v_longitudinal: filters?.hasDeltaVLongitudinal,
        has_delta_v_lateral: filters?.hasDeltaVLateral,
        has_accel_longitudinal: filters?.hasAccelLongitudinal,
        has_accel_lateral: filters?.hasAccelLateral,
        image_counts: filters?.imageCounts || [],
        image_count_at_least: filters?.imageCountAtLeast,
        image_perspectives: filters?.imagePerspectives || [],
        image_framings: filters?.imageFramings || [],
        image_focus_areas: filters?.imageFocusAreas || [],
        case_labels: filters?.caseLabels || [],
        limit: filters?.limit || 100,
        page: filters?.page || 1,
        sort_by: filters?.sortBy || '',
        sort_order: filters?.sortOrder || '',
        sort: filters?.sort && filters.sort.length > 0 ? filters.sort : undefined,
      };

      // Use publicFetchRaw to POST with JSON body
      const response = await this.publicFetchRaw<{
        success: boolean;
        data: Case[];
        pagination: {
          total: number;
          page: number;
          limit: number;
          totalPages: number;
          hasNextPage: boolean;
          hasPrevPage: boolean;
        };
        review?: CaseReviewProgress;
      }>('/datasets/cases/filter', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      return {
        data: response.data,
        pagination: response.pagination,
        review: response.review,
      };
    },

    // Get dataset by ID with cases (paginated)
    get: async (id: string, search?: string, page?: number, limit?: number): Promise<PaginatedDatasetResponse> => {
      const params: Record<string, string> = {};
      if (search) {
        params.search = search;
      }
      if (page) {
        params.page = page.toString();
      }
      if (limit) {
        params.limit = limit.toString();
      }
      // Use publicFetchRaw to get full response including pagination metadata and counts
      const response = await this.publicFetchRaw(`/datasets/${id}`, { params });
      return {
        data: response.data,
        pagination: response.pagination,
        counts: response.counts
      };
    },

    // Get case by ID from dataset
    getCase: (datasetId: string, caseId: string): Promise<Case> => {
      return this.publicFetch(`/datasets/${datasetId}/cases/${caseId}`);
    },

    // Start Delta-V inference workflow for a case
    inferDeltaV: (datasetId: string, caseId: string): Promise<{ success: boolean; workflowId?: string; runId?: string; message?: string }> => {
      return this.publicFetch(`/datasets/${datasetId}/cases/${caseId}/infer-delta-v`, {
        method: 'POST',
      });
    },

    // Get Delta-V inference workflow status
    getInferenceStatus: (workflowId: string): Promise<DeltaVInferenceResult> => {
      return this.publicFetch(`/datasets/inference/${workflowId}/status`);
    },

    // Vision-LLM sanity-check: Claude reviews the case photos and judges a
    // model's Delta-V prediction (independent estimate + verdict + reasoning).
    judgeDeltaV: (
      datasetId: string,
      caseId: string,
      payload: { modelVersion: string; predictedDeltaV: number },
    ): Promise<{
      success: boolean;
      message?: string;
      error?: string;
      data?: {
        estimate_mph: number;
        verdict: 'too_high' | 'plausible' | 'too_low';
        confidence: string;
        reasoning: string;
        predicted_delta_v: number;
        model_version: string;
        n_images: number;
        llm_model: string;
      };
      // publicFetchRaw (NOT publicFetch): keep the { success, data } envelope
      // intact. publicFetch's handleResponse strips it down to `data`, which drops
      // `success` and breaks the modal's `res.success && res.data` check.
    }> => {
      return this.publicFetchRaw(`/datasets/${datasetId}/cases/${caseId}/judge-deltav`, {
        method: 'POST',
        body: JSON.stringify({
          model_version: payload.modelVersion,
          predicted_delta_v: payload.predictedDeltaV,
        }),
      });
    },

    // Every persisted gut-check for a dataset, used to rehydrate the benchmark
    // grid on page load. publicFetchRaw to keep the { success, data } envelope.
    listDeltaVJudgments: (
      datasetId: string,
    ): Promise<{
      success: boolean;
      message?: string;
      error?: string;
      data?: Array<{
        dataset_case_id: string;
        model_version: string;
        predicted_delta_v: number;
        estimate_mph: number;
        verdict: 'too_high' | 'plausible' | 'too_low';
        confidence: string;
        reasoning: string;
        llm_model: string;
        n_images: number;
      }>;
    }> => {
      return this.publicFetchRaw(`/datasets/${datasetId}/judgments`);
    },

    // Create new dataset
    create: (request: CreateDatasetRequest): Promise<Dataset> => {
      return this.publicFetch('/datasets', {
        method: 'POST',
        body: JSON.stringify(request),
      });
    },

    // Update dataset
    update: (id: string, request: UpdateDatasetRequest): Promise<Dataset> => {
      return this.publicFetch(`/datasets/${id}`, {
        method: 'PUT',
        body: JSON.stringify(request),
      });
    },

    // Delete dataset
    delete: (id: string): Promise<void> => {
      return this.publicFetch(`/datasets/${id}`, {
        method: 'DELETE',
      });
    },

    // Extract EDR data from dataset
    // Fire-and-forget LLM EDR ground-truth extraction across all cases in a dataset.
    // onlyMissing (default true) skips cases that already have a successful extraction,
    // so existing ground-truth values are not overwritten.
    extractEDR: (
      id: string,
      opts?: { onlyMissing?: boolean },
    ): Promise<{ success: boolean; workflow_id?: string; only_missing?: boolean; error?: string }> => {
      return this.publicFetch(`/datasets/${id}/extract-edr`, {
        method: 'POST',
        body: JSON.stringify({
          only_missing: opts?.onlyMissing ?? true,
        }),
      });
    },

    // Fire-and-forget vector regeneration for all eligible cases in a dataset.
    regenerateVectors: (id: string): Promise<{ success: boolean; workflow_id?: string; error?: string }> => {
      return this.publicFetch(`/datasets/${id}/regenerate-vectors`, {
        method: 'POST',
      });
    },

    // Delete all of a dataset's case vectors from the vector store, removing them
    // from Similar Cases. Reversible — regenerateVectors rebuilds them via UPSERT.
    clearVectors: (id: string): Promise<{ success: boolean; deleted_count?: number; error?: string }> => {
      return this.publicFetch(`/datasets/${id}/vectors`, {
        method: 'DELETE',
      });
    },

    // Fire-and-forget LLM damage-score extraction across all cases in a dataset.
    // onlyMissing (default true) skips images that already have ground_truth_via_llm.
    extractDamageScores: (
      id: string,
      opts?: { onlyMissing?: boolean; llmModel?: string },
    ): Promise<{ success: boolean; workflow_id?: string; only_missing?: boolean; error?: string }> => {
      return this.publicFetch(`/datasets/${id}/extract-damage-scores`, {
        method: 'POST',
        body: JSON.stringify({
          only_missing: opts?.onlyMissing ?? true,
          llm_model: opts?.llmModel,
        }),
      });
    },

    // Start the unified enrichment workflows for a dataset: per-image LLM metadata
    // (perspective/framing/focus_area/labels), case-level vehicle metadata
    // (vehicle_type/year/make/model/curb weight), plus SAM3 segments when segmentation
    // is enabled on the backend (segments_workflow_id is empty otherwise). Each half
    // skips work that is already done (only_missing also re-extracts images whose
    // LLM metadata predates the current prompt schema), so the trigger is safe to
    // re-run.
    extractImageMetadata: (
      id: string,
      opts?: { onlyMissing?: boolean; llmModel?: string },
    ): Promise<{
      success: boolean;
      workflow_id?: string;
      segments_workflow_id?: string;
      vehicle_metadata_workflow_id?: string;
      only_missing?: boolean;
      error?: string;
    }> => {
      return this.publicFetch(`/datasets/${id}/extract-image-metadata`, {
        method: 'POST',
        body: JSON.stringify({
          only_missing: opts?.onlyMissing ?? true,
          llm_model: opts?.llmModel,
        }),
      });
    },

    // ML manifest export: zips the pointer-only CSV trio (manifest /
    // damage_scores / segments + README) for every dataset filed under a
    // category into the training bucket as datasets/<slug>/<slug>_<date>.zip.
    // The workflow id is deterministic per folder, so a second start while
    // one runs is a 409.
    startManifestExport: (category: string): Promise<ManifestExportStart> => {
      return this.publicFetch('/datasets/manifest-exports', {
        method: 'POST',
        body: JSON.stringify({ category }),
      });
    },

    getManifestExportStatus: (workflowId: string): Promise<ManifestExportStatus> => {
      return this.publicFetch(`/datasets/manifest-exports/${encodeURIComponent(workflowId)}/status`);
    },
  };

  // Cases API methods
  cases = {
    // Get cases for a dataset
    getDatasetCases: (datasetId: string): Promise<Case[]> => {
      return this.publicFetch(`/datasets/${datasetId}/cases`);
    },

    // Get case by ID with images - requires dataset context
    get: (id: string, datasetId: string): Promise<Case> => {
      return this.publicFetch(`/datasets/${datasetId}/cases/${id}`);
    },

    // Create new case in dataset
    create: (datasetId: string, request: CreateCaseRequest): Promise<Case> => {
      return this.publicFetch(`/datasets/${datasetId}/cases`, {
        method: 'POST',
        body: JSON.stringify(request),
      });
    },

    // Update case - requires dataset context
    update: (id: string, request: UpdateCaseRequest, datasetId: string): Promise<Case> => {
      return this.publicFetch(`/datasets/${datasetId}/cases/${id}`, {
        method: 'PUT',
        body: JSON.stringify(request),
      });
    },

    // Delete case - requires dataset context
    delete: (id: string, datasetId: string): Promise<void> => {
      return this.publicFetch(`/datasets/${datasetId}/cases/${id}`, {
        method: 'DELETE',
      });
    },

    // Upload images to case using multipart form data - requires dataset context
    uploadImages: (caseId: string, files: File[], datasetId: string): Promise<Image[]> => {
      const formData = new FormData();
      
      // Add all files to the form data
      files.forEach(file => {
        formData.append('files', file);
      });

      return this.publicFetch(`/datasets/${datasetId}/cases/${caseId}/files/upload`, {
        method: 'POST',
        body: formData,
      });
    },

    // Upload non-crash-photo file(s) to a case under an explicit category:
    // 'attachment' (default) or 'edr_document'. Either way the file is excluded from
    // crash-photo training/similar-case selection, regardless of file type.
    uploadAttachment: (caseId: string, files: File[], datasetId: string, category: 'attachment' | 'edr_document' = 'attachment'): Promise<Image[]> => {
      const formData = new FormData();

      files.forEach(file => {
        formData.append('files', file);
      });
      formData.append('category', category);

      return this.publicFetch(`/datasets/${datasetId}/cases/${caseId}/attachments/upload`, {
        method: 'POST',
        body: formData,
      });
    },

    // Update ground truth for a file - requires dataset context
    updateImageGroundTruth: (imageId: string, groundTruth: GroundTruthData, datasetId: string): Promise<Image> => {
      return this.publicFetch(`/datasets/${datasetId}/files/${imageId}/ground-truth`, {
        method: 'PUT',
        body: JSON.stringify(groundTruth),
      });
    },

    // Update ground truth for a case - requires dataset context
    updateCaseGroundTruth: (caseId: string, groundTruth: GroundTruthData, datasetId: string): Promise<Case> => {
      return this.publicFetch(`/datasets/${datasetId}/cases/${caseId}/ground-truth`, {
        method: 'PUT',
        body: JSON.stringify(groundTruth),
      });
    },

    // Flag/unflag an image as not relevant for ML training - requires dataset context.
    // Persists exclude_from_training in the file's annotations; image is kept, just skipped by export.
    setImageTrainingExclusion: (imageId: string, excluded: boolean, datasetId: string): Promise<Image> => {
      return this.publicFetch(`/datasets/${datasetId}/files/${imageId}/training-exclusion`, {
        method: 'PUT',
        body: JSON.stringify({ excluded }),
      });
    },

    // Save human-edited per-image metadata (perspective, framing, focus area, labels) -
    // requires dataset context. Persisted in annotations.image_metadata; values are
    // validated server-side against the closed vocabularies (400 on violation).
    updateImageMetadata: (imageId: string, metadata: ImageMetadata, datasetId: string): Promise<Image> => {
      return this.publicFetch(`/datasets/${datasetId}/files/${imageId}/image-metadata`, {
        method: 'PUT',
        body: JSON.stringify(metadata),
      });
    },

    // Fetch the precomputed SAM3 segmentation result for an image - requires dataset
    // context. Throws ApiError(404) when segments have not been computed yet.
    getImageSegments: (imageId: string, datasetId: string): Promise<DatasetFileSegments> => {
      return this.publicFetch(`/datasets/${datasetId}/files/${imageId}/segments`);
    },

    // Set the EDR manufacturer/format (e.g. Bosch, Tesla) for a single EDR file -
    // requires dataset context. Persisted per-file in the file's annotations.
    setFileEDRFormat: (fileId: string, format: string | undefined, datasetId: string): Promise<Image> => {
      return this.publicFetch(`/datasets/${datasetId}/files/${fileId}/edr-format`, {
        method: 'PUT',
        body: JSON.stringify({ format: format ?? '' }),
      });
    },

    // Delete a file - requires dataset context
    deleteImage: (imageId: string, datasetId: string): Promise<void> => {
      return this.publicFetch(`/datasets/${datasetId}/files/${imageId}`, {
        method: 'DELETE',
      });
    },

    // Extract EDR for a single dataset case
    extractEDR: (caseId: string, datasetId: string): Promise<{ success: boolean; workflow_id?: string; error?: string }> => {
      return this.publicFetch(`/datasets/${datasetId}/cases/${caseId}/extract-edr`, {
        method: 'POST',
      });
    },

    // Start the unified enrichment workflows for a SINGLE case: per-image LLM metadata
    // (perspective/framing/focus_area/labels), case-level vehicle metadata, plus SAM3
    // segments when segmentation is enabled on the backend (segments_workflow_id is
    // empty otherwise). Each half skips work that is already done (only_missing also
    // re-extracts images whose LLM metadata predates the current prompt schema), so
    // re-running on an up-to-date case is a no-op.
    extractImageMetadata: (
      caseId: string,
      datasetId: string,
      opts?: { onlyMissing?: boolean; llmModel?: string },
    ): Promise<{
      success: boolean;
      message?: string;
      workflow_id?: string;
      segments_workflow_id?: string;
      vehicle_metadata_workflow_id?: string;
      only_missing?: boolean;
      error?: string;
    }> => {
      return this.publicFetch(`/datasets/${datasetId}/cases/${caseId}/extract-image-metadata`, {
        method: 'POST',
        body: JSON.stringify({
          only_missing: opts?.onlyMissing ?? true,
          llm_model: opts?.llmModel,
        }),
      });
    },

    // Update EDR ground truth for a case - requires dataset context
    updateEDRTargets: (caseId: string, edrTargets: EDRTargets, datasetId: string): Promise<Case> => {
      return this.publicFetch(`/datasets/${datasetId}/cases/${caseId}`, {
        method: 'PUT',
        body: JSON.stringify({ edr_ground_truth: edrTargets }),
      });
    },

    // Enhance ground truth for a file using LLM
    enhanceImageGroundTruth: (imageId: string, datasetId: string | undefined, payload: { provider: string; prompt: string; image_url: string }): Promise<any> => {
      const endpoint = datasetId
        ? `/datasets/${datasetId}/files/${imageId}/enhance-ground-truth`
        : `/files/${imageId}/enhance-ground-truth`;

      return this.publicFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },

    // Flag a dataset case with an optional note and category
    flagCase: (caseId: string, datasetId: string, note?: string, category?: string): Promise<Case> => {
      return this.publicFetch(`/datasets/${datasetId}/cases/${caseId}/flag`, {
        method: 'PUT',
        body: JSON.stringify({
          note: note || '',
          category: category || undefined
        }),
      });
    },

    // Remove flag from a dataset case
    unflagCase: (caseId: string, datasetId: string): Promise<Case> => {
      return this.publicFetch(`/datasets/${datasetId}/cases/${caseId}/flag`, {
        method: 'DELETE',
      });
    },

    // Confirm a dataset case as reviewed · correct (sets reviewed_at)
    reviewCase: (caseId: string, datasetId: string): Promise<Case> => {
      return this.publicFetch(`/datasets/${datasetId}/cases/${caseId}/review`, {
        method: 'PUT',
      });
    },

    // Withdraw the review confirmation (clears reviewed_at)
    unreviewCase: (caseId: string, datasetId: string): Promise<Case> => {
      return this.publicFetch(`/datasets/${datasetId}/cases/${caseId}/review`, {
        method: 'DELETE',
      });
    },

    // Dismiss the Delta-V plausibility verdict (over/under-estimate flag) for a case.
    // Writes a human override so the LLM audit enrichment won't re-flag it later.
    dismissDeltaVAudit: (caseId: string, datasetId: string): Promise<Case> => {
      return this.publicFetch(`/datasets/${datasetId}/cases/${caseId}/delta-v-audit`, {
        method: 'DELETE',
      });
    },

    // Move a case to a different dataset. Files, similarity vector and GCS objects are
    // keyed by case_id and travel with the case; only the case's dataset_id changes.
    moveCase: (caseId: string, datasetId: string, targetDatasetId: string): Promise<Case> => {
      return this.publicFetch(`/datasets/${datasetId}/cases/${caseId}/move`, {
        method: 'PUT',
        body: JSON.stringify({ target_dataset_id: targetDatasetId }),
      });
    },

    // Find similar cases using vector similarity search
    findSimilarCases: (caseId: string, datasetId: string, limit: number = 3): Promise<SimilarCase[]> => {
      return this.publicFetch(`/datasets/${datasetId}/cases/${caseId}/similar`, {
        params: { limit: limit.toString() },
      });
    },
  };

  // Prompts API methods
  prompts = {
    // Get all prompts
    getAll: (userId?: string): Promise<Prompt[]> => {
      const params: Record<string, string> = {};
      if (userId) {
        params.user_id = userId;
      }
      return this.publicFetch('/prompts', { params });
    },

    // Get prompt by ID
    get: (id: string): Promise<Prompt> => {
      return this.publicFetch(`/prompts/${id}`);
    },

    // Create new prompt
    create: (request: CreatePromptRequest): Promise<Prompt> => {
      return this.publicFetch('/prompts', {
        method: 'POST',
        body: JSON.stringify(request),
      });
    },

    // Update prompt
    update: (id: string, request: UpdatePromptRequest): Promise<Prompt> => {
      return this.publicFetch(`/prompts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(request),
      });
    },

    // Delete prompt
    delete: (id: string): Promise<void> => {
      return this.publicFetch(`/prompts/${id}`, {
        method: 'DELETE',
      });
    },

    // Search prompts
    search: (query: string, userId?: string): Promise<Prompt[]> => {
      const params: Record<string, string> = { q: query };
      if (userId) {
        params.user_id = userId;
      }
      return this.publicFetch('/prompts/search', { params });
    },

    // Get supported template variables for a prompt type
    getVariables: (promptType: string): Promise<PromptVariableGroup[]> => {
      return this.publicFetch('/prompts/variables', {
        params: { type: promptType },
      });
    },

    // Set prompt as production/live
    setProduction: (id: string): Promise<void> => {
      return this.publicFetch(`/prompts/${id}/production`, {
        method: 'PUT',
      });
    },

    // Test EDR extraction with uploaded PDF file
    testEDRExtraction: (promptId: string, file: File): Promise<{
      extraction_result: unknown;
      raw_response: string;
      processing_time_seconds: number;
    }> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('prompt_id', promptId);

      return this.publicFetch('/prompts/test-edr-extraction', {
        method: 'POST',
        body: formData,
        headers: {} // Remove default Content-Type to allow FormData boundary
      });
    },

    // Test TCR extraction with uploaded PDF file
    testTCRExtraction: (promptId: string, file: File): Promise<{
      extraction_result: unknown;
      raw_response: string;
      processing_time_seconds: number;
    }> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('prompt_id', promptId);

      return this.publicFetch('/prompts/test-tcr-extraction', {
        method: 'POST',
        body: formData,
        headers: {} // Remove default Content-Type to allow FormData boundary
      });
    },

    // Test vehicle extraction with uploaded PDF file
    testVehicleExtraction: (promptId: string, file: File): Promise<{
      extraction_result: unknown;
      processing_time: number;
      status: 'success' | 'error';
      message?: string;
    }> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('prompt_id', promptId);

      return this.publicFetch('/prompts/test-vehicle-extraction', {
        method: 'POST',
        body: formData,
        headers: {} // Remove default Content-Type to allow FormData boundary
      });
    },

    // Test damage score assessment with uploaded image file
    testDamageScore: (promptId: string, file: File): Promise<{
      damage_scores: GroundTruthData;
      raw_response?: string;
      processing_time_seconds: number;
    }> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('prompt_id', promptId);

      return this.publicFetch('/prompts/test-damage-score', {
        method: 'POST',
        body: formData,
        headers: {} // Remove default Content-Type to allow FormData boundary
      });
    },
  };

  // Evaluation Runs API methods
  evaluationRuns = {
    // Get all evaluation runs
    getAll: (promptId?: string, userId?: string): Promise<EvaluationRun[]> => {
      const params: Record<string, string> = {};
      if (promptId) {
        params.prompt_id = promptId;
      }
      if (userId) {
        params.user_id = userId;
      }
      return this.authFetch('/evaluation-runs', { params });
    },

    // Get evaluation run by ID with optional threshold
    get: (id: string, threshold?: number): Promise<EvaluationRun> => {
      const params: Record<string, string> = {};
      if (threshold !== undefined) {
        params.threshold = threshold.toString();
      }
      return this.authFetch(`/evaluation-runs/${id}`, { params });
    },

    // Get evaluation runs by prompt ID
    getByPromptId: (promptId: string): Promise<EvaluationRun[]> => {
      return this.authFetch(`/prompts/${promptId}/evaluation-runs`);
    },

    // Create new evaluation run
    create: (request: CreateEvaluationRunRequest): Promise<EvaluationRun> => {
      return this.authFetch('/evaluation-runs/async', {
        method: 'POST',
        body: JSON.stringify(request),
      });
    },

    // Update evaluation run
    update: (id: string, request: UpdateEvaluationRunRequest): Promise<EvaluationRun> => {
      return this.authFetch(`/evaluation-runs/${id}`, {
        method: 'PUT',
        body: JSON.stringify(request),
      });
    },

    // Delete evaluation run
    delete: (id: string): Promise<void> => {
      return this.authFetch(`/evaluation-runs/${id}`, {
        method: 'DELETE',
      });
    },

    // Get error details for evaluation run
    getErrorDetails: (id: string): Promise<ErrorDetails> => {
      return this.authFetch(`/evaluation-runs/${id}/error-details`);
    },

    // Cancel a running evaluation run
    cancel: (id: string, reason?: string): Promise<{ message: string }> => {
      const params: Record<string, string> = {};
      if (reason) params.reason = reason;
      return this.authFetch(`/evaluation-runs/${id}/cancel`, {
        method: 'POST',
        params,
      });
    },

    // Get ML inference predictions for evaluation run (list with summary)
    getMLPredictions: (runId: string): Promise<import('@/types').MLInferencePredictionsListResponse> => {
      return this.authFetch(`/evaluation-runs/${runId}/ml-predictions`);
    },

    // Get ML inference prediction detail for specific case
    getMLPredictionDetail: (runId: string, caseId: string): Promise<import('@/types').MLInferencePredictionDetail> => {
      return this.authFetch(`/evaluation-runs/${runId}/ml-predictions/${caseId}`);
    },

    // Multi-variant (v2) ML inference predictions. Used by runs created with
    // a config.variants list; the v2 page renders one column per variant.
    getMLPredictionsV2: (runId: string): Promise<import('@/types').MLInferencePredictionsV2ListResponse> => {
      return this.authFetch(`/evaluation-runs/${runId}/ml-predictions-v2`);
    },
  };

  // PDOF Evaluation API methods
  // Backend endpoints land next turn under /pdof-evaluation-runs.
  pdofEvaluation = {
    getAll: (): Promise<PDOFEvaluationRun[]> => {
      return this.authFetch('/pdof-evaluation-runs');
    },

    get: (id: string): Promise<PDOFEvaluationRun> => {
      return this.authFetch(`/pdof-evaluation-runs/${id}`);
    },

    create: (request: CreatePDOFEvaluationRunRequest): Promise<PDOFEvaluationRun> => {
      return this.authFetch('/pdof-evaluation-runs', {
        method: 'POST',
        body: JSON.stringify(request),
      });
    },

    delete: (id: string): Promise<void> => {
      return this.authFetch(`/pdof-evaluation-runs/${id}`, {
        method: 'DELETE',
      });
    },

    cancel: (id: string, reason?: string): Promise<{ message: string }> => {
      const params: Record<string, string> = {};
      if (reason) params.reason = reason;
      return this.authFetch(`/pdof-evaluation-runs/${id}/cancel`, {
        method: 'POST',
        params,
      });
    },

    getResults: (id: string): Promise<PDOFCaseResultsResponse> => {
      return this.authFetch(`/pdof-evaluation-runs/${id}/results`);
    },
  };

  // Question Bank API methods
  questionBank = {
    // Get all questions with filtering and pagination
    getAll: (filters?: QuestionBankFilters): Promise<QuestionBankListResponse> => {
      const params: Record<string, string> = {};
      
      if (filters) {
        if (filters.discipline) params.discipline = filters.discipline;
        if (filters.search) params.search = filters.search;
        if (filters.page) params.page = filters.page.toString();
        if (filters.limit) params.limit = filters.limit.toString();
        if (filters.questionTypes?.length) params.questionTypes = filters.questionTypes.join(',');
        if (filters.strategicLabels?.length) params.strategicLabels = filters.strategicLabels.join(',');
        if (filters.categories?.length) params.categories = filters.categories.join(',');
        if (filters.forDeposition !== undefined) params.forDeposition = filters.forDeposition.toString();
        if (filters.forTrial !== undefined) params.forTrial = filters.forTrial.toString();
      }
      
      return this.publicFetch('/question-bank', { params });
    },

    // Get single question by ID
    get: (id: string): Promise<QuestionBank> => {
      return this.publicFetch(`/question-bank/${id}`);
    },

    // Create new question
    create: (request: CreateQuestionBankRequest): Promise<QuestionBank> => {
      return this.publicFetch('/question-bank', {
        method: 'POST',
        body: JSON.stringify(request),
      });
    },

    // Update existing question
    update: (id: string, request: UpdateQuestionBankRequest): Promise<QuestionBank> => {
      return this.publicFetch(`/question-bank/${id}`, {
        method: 'PUT',
        body: JSON.stringify(request),
      });
    },

    // Delete question
    delete: (id: string): Promise<void> => {
      return this.publicFetch(`/question-bank/${id}`, {
        method: 'DELETE',
      });
    },

    // Get filter options (distinct values for dropdowns)
    getFilterOptions: (): Promise<QuestionBankFilterOptions> => {
      return this.publicFetch('/question-bank/filter-options');
    },

    // Import questions from CSV file
    importCSV: (file: File): Promise<{ message: string; totalImported: number; totalLines: number; filename: string; fileSize: number }> => {
      const formData = new FormData();
      formData.append('file', file);
      
      return this.publicFetch('/question-bank/import', {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header - let browser set it with boundary for FormData
        headers: {
          // Remove default Content-Type to allow FormData boundary
        },
      });
    },
  };

  // Research Papers API methods
  researchPapers = {
    // Search research papers with filtering and pagination
    search: async (filters?: ResearchPaperFilters): Promise<ResearchPaperListResponse> => {
      const params: Record<string, string> = {};
      
      if (filters) {
        if (filters.search) params.q = filters.search;
        if (filters.page) params.offset = ((filters.page - 1) * filters.limit).toString();
        if (filters.limit) params.limit = filters.limit.toString();
        if (filters.yearFrom) params.yearFrom = filters.yearFrom.toString();
        if (filters.yearTo) params.yearTo = filters.yearTo.toString();
        if (filters.hasDOI !== undefined) params.hasDOI = filters.hasDOI.toString();
        if (filters.hasAbstract !== undefined) params.hasAbstract = filters.hasAbstract.toString();
        if (filters.hasPublication !== undefined) params.hasPublication = filters.hasPublication.toString();
        if (filters.tags && filters.tags.length > 0) params.tags = filters.tags.join(',');
        if (filters.sortBy) params.sort_by = filters.sortBy;
        if (filters.sortOrder) params.sort_order = filters.sortOrder;
      }
      
      // Get backend response - handleResponse already unwraps the data
      const backendResponse = await this.publicFetch<{
        papers: ResearchPaper[];
        total: number;
        limit: number;
        offset: number;
        total_pages: number;
      }>('/research-papers/search', { params });

      // Transform to frontend expected format (page/pages)
      const page = Math.floor(backendResponse.offset / backendResponse.limit) + 1;
      
      return {
        papers: backendResponse.papers,
        total: backendResponse.total,
        limit: backendResponse.limit,
        page: page,
        pages: backendResponse.total_pages,
      };
    },

    // Get all unique tags from research papers
    getTags: (): Promise<{ tags: string[] }> => {
      return this.publicFetch('/research-papers/tags');
    },

    // Get single research paper by ID
    get: (id: string): Promise<ResearchPaper> => {
      return this.publicFetch(`/research-papers/${id}`);
    },

    // Upload PDF and extract metadata with LLM
    upload: (file: File): Promise<ResearchPaperUploadResponse> => {
      const formData = new FormData();
      formData.append('file', file);
      
      return this.publicFetch('/research-papers/upload', {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header - let browser set it with boundary for FormData
        headers: {},
      });
    },

    // Upload CSV for bulk import
    uploadCSV: (file: File, useLLM: boolean = true): Promise<any> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('use_llm', useLLM.toString());
      
      return this.publicFetch('/research-papers/upload-csv', {
        method: 'POST',
        body: formData,
        headers: {},
      });
    },

    // Delete research paper (if admin endpoints are ever needed)
    delete: (id: string): Promise<void> => {
      return this.publicFetch(`/research-papers/${id}`, {
        method: 'DELETE',
      });
    },
  };

  // ML Training API methods
  mlTraining = {
    // Get paginated ML training runs. refresh=true bypasses the backend's
    // storage model cache — used by the manual refresh button to pick up
    // models uploaded directly to GCS (which the API can't observe).
    getRuns: (page: number = 1, pageSize: number = 20, refresh: boolean = false): Promise<MLTrainingRunListResponse> => {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });
      if (refresh) {
        params.set('refresh', 'true');
      }
      return this.publicFetch(`/ml-training?${params}`);
    },

    // Benchmark baseline for the v2 predictions page: every case of every
    // is_evaluation dataset with ground truth + images, no variants. The
    // benchmark page starts from this and overlays models as comparisons.
    getBenchmarkCases: (): Promise<import('@/types').MLInferencePredictionsV2ListResponse> => {
      return this.authFetch('/ml-training/benchmark-cases');
    },

    // Get paginated ML training runs enriched with completed evaluation metrics.
    getLeaderboard: (page: number = 1, pageSize: number = 20, refresh: boolean = false): Promise<MLTrainingRunListResponse> => {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });
      if (refresh) {
        params.set('refresh', 'true');
      }
      return this.publicFetch(`/ml-training/leaderboard?${params}`);
    },

    // Trigger a new ML training run
    triggerTraining: (request: MLTrainingRunRequest): Promise<MLTrainingRun> => {
      return this.publicFetch('/ml-training', {
        method: 'POST',
        body: JSON.stringify(request),
        headers: {
          'Content-Type': 'application/json',
        },
      });
    },

    // Get status of a specific ML training run
    getStatus: (workflowId: string): Promise<MLTrainingRun> => {
      return this.publicFetch(`/ml-training/${workflowId}/status`);
    },

    // Cancel a running ML training workflow
    cancel: (workflowId: string): Promise<MLTrainingRun> => {
      return this.publicFetch(`/ml-training/${workflowId}/cancel`, {
        method: 'DELETE',
      });
    },

    // Set a model as production
    setProduction: (workflowId: string): Promise<MLTrainingRun> => {
      return this.publicFetch(`/ml-training/${workflowId}/production`, {
        method: 'PUT',
      });
    },

    // Delete an ML training version
    delete: (workflowId: string): Promise<void> => {
      return this.publicFetch(`/ml-training/${workflowId}`, {
        method: 'DELETE',
      });
    },
  };

  // ML Inference API methods
  mlInference = {
    // Test a model with sample data
    testModel: (request: MLInferenceRequest): Promise<MLInferenceResponse> => {
      return this.publicFetch('/ml-inference/test', {
        method: 'POST',
        body: JSON.stringify(request),
        headers: {
          'Content-Type': 'application/json',
        },
      });
    },

    // Test a model with sample data AND vector similarity search
    testModelWithSimilarity: (request: MLInferenceRequest): Promise<CombinedInferenceResponse> => {
      return this.publicFetch('/ml-inference/test-with-similarity', {
        method: 'POST',
        body: JSON.stringify(request),
        headers: {
          'Content-Type': 'application/json',
        },
      });
    },

    // Get inference status (for polling)
    getInferenceStatus: (workflowId: string): Promise<MLInferenceStatus> => {
      return this.publicFetch(`/ml-inference/${workflowId}/status`);
    },
  };

  // EDR Extraction Evaluation API methods (Temporal-only, no DB persistence)
  edrEvaluations = {
    // Start a new EDR extraction evaluation for a dataset
    create: (
      datasetId: string,
      groundTruthSource: 'human' | 'llm' = 'human',
      llmProvider?: string,
      llmModel?: string,
      datasetCaseId?: string
    ): Promise<{ workflow_id: string; run_id: string; dataset_id: string; dataset_name: string; ground_truth_source: string; llm_provider?: string; llm_model?: string; message: string }> => {
      return this.publicFetch('/datasets/evaluations/edr-extraction', {
        method: 'POST',
        body: JSON.stringify({
          dataset_id: datasetId,
          ground_truth_source: groundTruthSource,
          llm_provider: llmProvider,
          llm_model: llmModel,
          ...(datasetCaseId && { dataset_case_id: datasetCaseId }),
        }),
      });
    },

    // List recent evaluation runs
    list: (limit?: number): Promise<{ evaluations: any[]; count: number }> => {
      const params = limit ? { limit: limit.toString() } : undefined;
      return this.publicFetch('/datasets/evaluations/edr-extraction', { params });
    },

    // Get complete evaluation result
    get: (workflowId: string, runId?: string): Promise<any> => {
      const params = runId ? { run_id: runId } : undefined;
      return this.publicFetch(`/datasets/evaluations/edr-extraction/${workflowId}`, { params });
    },

    // Get evaluation status
    getStatus: (workflowId: string, runId?: string): Promise<{ workflow_id: string; run_id: string; status: string; start_time?: string; close_time?: string }> => {
      const params = runId ? { run_id: runId } : undefined;
      return this.publicFetch(`/datasets/evaluations/edr-extraction/${workflowId}/status`, { params });
    },

    // Cancel a running evaluation
    cancel: (workflowId: string, runId?: string, reason?: string): Promise<{ message: string }> => {
      const params: Record<string, string> = {};
      if (runId) params.run_id = runId;
      if (reason) params.reason = reason;
      return this.publicFetch(`/datasets/evaluations/edr-extraction/${workflowId}`, {
        method: 'DELETE',
        params,
      });
    },

    // Delete/terminate an evaluation (marks as terminated so it won't show in list)
    delete: (workflowId: string, runId?: string, reason?: string): Promise<{ message: string }> => {
      const params: Record<string, string> = {};
      if (runId) params.run_id = runId;
      if (reason) params.reason = reason;
      return this.publicFetch(`/datasets/evaluations/edr-extraction/${workflowId}/terminate`, {
        method: 'DELETE',
        params,
      });
    },
  };

  // Delta-V Inference API methods
  deltaVInference = {
    // Start Delta-V inference for a case via images (synchronous - blocks until complete)
    start: (caseId: string, datasetId: string): Promise<DeltaVInferenceResult> => {
      return this.publicFetch(`/datasets/${datasetId}/cases/${caseId}/infer-deltav-via-images`, {
        method: 'POST',
      });
    },

    // Get inference result
    get: (inferenceId: string): Promise<any> => {
      return this.publicFetch(`/delta-v-inference/${inferenceId}`);
    },

    // Get inference status (for polling - currently not implemented)
    getStatus: (workflowId: string): Promise<{ status: 'running' | 'completed' | 'failed'; result?: any }> => {
      return this.publicFetch(`/delta-v-inference/status/${workflowId}`);
    },
  };

  // Biomechanics Workflow API methods
  biomechanics = {
    // Execute biomechanics with py-bio-engines. Results include engine_source
    // and bio_engines_raw with per-engine outputs.
    execute: (input: Record<string, unknown>): Promise<Record<string, unknown>> => {
      return this.publicFetch('/biomechanics/execute', {
        method: 'POST',
        body: JSON.stringify({
          ...input,
          testMode: true,
        }),
      });
    },

    // Get workflow status (for polling)
    getStatus: (workflowId: string): Promise<{ workflow_id: string; status: string; result?: Record<string, unknown> }> => {
      return this.publicFetch(`/biomechanics/workflow/${workflowId}/status`);
    },

    // Get workflow result
    getResult: (workflowId: string): Promise<Record<string, unknown>> => {
      return this.publicFetch(`/biomechanics/workflow/${workflowId}`);
    },

    // Calculate AIS injury risk probabilities (synchronous, sub-millisecond)
    calculateAISRisk: (input: Record<string, unknown>): Promise<Record<string, unknown>> => {
      return this.publicFetch('/biomechanics/ais-risk', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
  };

  // Technical Report Workflow API methods
  technicalReport = {
    // Execute technical report workflow with custom input (TEST MODE - doesn't save to DB)
    execute: (input: Record<string, unknown>): Promise<Record<string, unknown>> => {
      return this.publicFetch('/technical-report/execute', {
        method: 'POST',
        body: JSON.stringify({
          ...input,
          testMode: true, // Always use test mode in datasets app - don't persist to DB
        }),
      });
    },

    // Get workflow status (for polling)
    getStatus: (workflowId: string): Promise<{ workflow_id: string; status: string; result?: Record<string, unknown> }> => {
      return this.publicFetch(`/technical-report/workflow/${workflowId}/status`);
    },
  };

  // Damage Scores Extraction API methods
  damageScores = {
    // Extract damage scores for dataset files via LLM workflow
    extract: (
      datasetId: string,
      caseId: string,
      payload: {
        photoFileIds: string[];
        pdofDescription: string;
        vehicleType?: string;
        provider?: string;
      }
    ): Promise<{
      success: boolean;
      message: string;
      workflowId: string;
      runId: string;
      datasetId: string;
      caseId: string;
    }> => {
      return this.publicFetch(`/datasets/${datasetId}/cases/${caseId}/extract-damage-scores`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },

    // Get workflow status for damage score extraction
    getWorkflowStatus: (workflowId: string, params?: {
      limit?: number;
      minSimilarity?: number;
      finalMaxDeltaV?: number;
      pdofAngle?: number;
      vehicleType?: string;
    }): Promise<{
      success: boolean;
      workflowId: string;
      runId: string;
      status: 'running' | 'completed' | 'failed' | 'cancelled' | 'terminated' | 'timeout' | 'unknown';
      result?: Record<string, unknown>;
      damageScores?: Record<string, number>;
      similarCases?: SimilarCase[];
      similarCasesError?: string;
      error?: string;
    }> => {
      const query: Record<string, string> = {};
      if (params?.limit) {
        query.limit = String(params.limit);
      }
      if ((params?.minSimilarity ?? 0) > 0) {
        query.min_similarity = String(params?.minSimilarity);
      }
      if (params?.finalMaxDeltaV !== undefined) {
        query.final_max_delta_v = String(params.finalMaxDeltaV);
      }
      if (params?.pdofAngle !== undefined) {
        query.pdof_angle = String(params.pdofAngle);
      }
      if (params?.vehicleType) {
        query.vehicle_type = params.vehicleType;
      }
      const requestConfig = Object.keys(query).length > 0 ? { params: query } : undefined;
      return this.publicFetch(`/datasets/inference/${workflowId}/status`, requestConfig);
    },
  };

  // Vehicle Types API methods (Single Source of Truth)
  vehicleTypes = {
    // Get all vehicle type options for dropdowns
    // Frontend should NOT hardcode vehicle types - always fetch from this endpoint
    getOptions: (): Promise<VehicleTypeOption[]> => {
      return this.publicFetch('/vehicle-types');
    },
  };

  // System API methods
  system = {
    // Get enabled LLM providers
    getEnabledProviders: (): Promise<EnabledProvidersResponse> => {
      return this.publicFetch('/system/enabled-providers');
    },
  };

  // Vehicle Part Segmentation API methods (SAM3 Worker)
  segmentation = {
    // Upload a temporary image file for segmentation
    uploadImage: (file: File): Promise<SegmentationUploadResponse> => {
      const formData = new FormData();
      formData.append('file', file);
      return this.publicFetch('/segmentation/upload', {
        method: 'POST',
        body: formData,
        headers: {},  // Let browser set Content-Type with boundary for FormData
      });
    },

    // Start segmentation workflow
    run: (request: SegmentationRunRequest): Promise<SegmentationRunResponse> => {
      return this.publicFetch('/segmentation/run', {
        method: 'POST',
        body: JSON.stringify(request),
        headers: {
          'Content-Type': 'application/json',
        },
      });
    },

    // Get segmentation workflow status (for polling)
    getStatus: (workflowId: string): Promise<SegmentationStatusResponse> => {
      return this.publicFetch(`/segmentation/${workflowId}/status`);
    },
  };

  // Image Quality Assessment API methods
  imageQuality = {
    // Upload a temporary image file for quality assessment
    uploadImage: (file: File): Promise<ImageQualityUploadResponse> => {
      const formData = new FormData();
      formData.append('file', file);
      return this.publicFetch('/image-quality/upload', {
        method: 'POST',
        body: formData,
        headers: {},  // Let browser set Content-Type with boundary for FormData
      });
    },

    // Start image quality assessment workflow
    run: (request: ImageQualityRunRequest): Promise<ImageQualityRunResponse> => {
      return this.publicFetch('/image-quality/run', {
        method: 'POST',
        body: JSON.stringify(request),
        headers: {
          'Content-Type': 'application/json',
        },
      });
    },

    // Get image quality workflow status (for polling)
    getStatus: (workflowId: string): Promise<ImageQualityStatusResponse> => {
      return this.publicFetch(`/image-quality/${workflowId}/status`);
    },
  };

  // Intake Evidence Classification — standalone harness (Phase A classify +
  // Phase B owner routing). Unauth /apiv2 routes, same group as image-quality.
  intakeClassification = {
    // Upload a temporary evidence file (photo/PDF/video) for classification.
    uploadFile: (file: File): Promise<IntakeClassificationUploadResponse> => {
      const formData = new FormData();
      formData.append('file', file);
      return this.publicFetch('/intake-classification/upload', {
        method: 'POST',
        body: formData,
        headers: {}, // Let the browser set the multipart boundary for FormData
      });
    },

    // Start the classification + reconciliation workflow over the uploaded files.
    run: (
      tempFiles: IntakeClassificationTempFile[],
      opts?: {
        model?: string;
        plaintiffName?: string;
        defendantName?: string;
        occupants?: IntakeOccupantInput[];
      },
    ): Promise<IntakeClassificationRunResponse> => {
      return this.publicFetch('/intake-classification/run', {
        method: 'POST',
        body: JSON.stringify({
          temp_files: tempFiles,
          model: opts?.model,
          plaintiff_name: opts?.plaintiffName,
          defendant_name: opts?.defendantName,
          occupants: opts?.occupants,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      });
    },

    // Get classification workflow status (for polling).
    getStatus: (workflowId: string): Promise<IntakeClassificationStatusResponse> => {
      return this.publicFetch(`/intake-classification/${workflowId}/status`);
    },
  };

  // PDOF Calculator API methods
  pdofTool = {
    // Upload a temporary image file for standalone PDOF inference
    uploadImage: (file: File): Promise<PDOFToolUploadResponse> => {
      const formData = new FormData();
      formData.append('file', file);
      return this.publicFetch('/pdof-tool/upload', {
        method: 'POST',
        body: formData,
        headers: {},  // Let browser set Content-Type with boundary for FormData
      });
    },

    // Start standalone PDOF inference workflow
    run: (request: PDOFToolRunRequest): Promise<PDOFToolRunResponse> => {
      return this.publicFetch('/pdof-tool/run', {
        method: 'POST',
        body: JSON.stringify(request),
        headers: {
          'Content-Type': 'application/json',
        },
      });
    },

    // Get PDOF workflow status (for polling)
    getStatus: (workflowId: string): Promise<PDOFToolStatusResponse> => {
      return this.publicFetch(`/pdof-tool/${workflowId}/status`);
    },
  };

  // Crash Severity test tool — synchronous LLM classification (no polling).
  crashSeverityTool = {
    classify: (params: {
      images: File[];
      pdofAngle?: string;
      pdofDirection?: string;
      model?: string;
    }): Promise<CrashSeverityToolResult> => {
      const formData = new FormData();
      params.images.forEach(file => formData.append('images', file));
      if (params.pdofAngle) formData.append('pdof_angle', params.pdofAngle);
      if (params.pdofDirection) formData.append('pdof_direction', params.pdofDirection);
      if (params.model) formData.append('model', params.model);
      return this.publicFetch('/crash-severity-tool/classify', {
        method: 'POST',
        body: formData,
        headers: {}, // Let browser set the multipart boundary
      });
    },
  };

  // Delta-V Calculator API methods
  deltaVTool = {
    // Upload a temporary image file for standalone Delta-V inference
    uploadImage: (file: File): Promise<DeltaVToolUploadResponse> => {
      const formData = new FormData();
      formData.append('file', file);
      return this.publicFetch('/delta-v-tool/upload', {
        method: 'POST',
        body: formData,
        headers: {},  // Let browser set Content-Type with boundary for FormData
      });
    },

    // Start standalone Delta-V inference workflow
    run: (request: DeltaVToolRunRequest): Promise<DeltaVToolRunResponse> => {
      return this.publicFetch('/delta-v-tool/run', {
        method: 'POST',
        body: JSON.stringify(request),
        headers: {
          'Content-Type': 'application/json',
        },
      });
    },

    // Get Delta-V workflow status (for polling)
    getStatus: (workflowId: string): Promise<DeltaVToolStatusResponse> => {
      return this.publicFetch(`/delta-v-tool/${workflowId}/status`);
    },

    // List the curated ML Local hybrid models available for the Delta-V tool.
    getMLLocalModels: (): Promise<MLLocalModelsResponse> => {
      return this.publicFetch('/delta-v-tool/ml-local-models');
    },
  };

  // Hybrid Model Inference API methods
  hybridInference = {
    // Upload a temporary image file for hybrid inference
    uploadImage: (file: File): Promise<HybridInferenceUploadResponse> => {
      const formData = new FormData();
      formData.append('file', file);
      return this.publicFetch('/hybrid-inference/upload', {
        method: 'POST',
        body: formData,
        headers: {},  // Let browser set Content-Type with boundary for FormData
      });
    },

    // Start hybrid inference workflow
    run: (request: HybridInferenceRunRequest): Promise<HybridInferenceRunResponse> => {
      return this.publicFetch('/hybrid-inference/run', {
        method: 'POST',
        body: JSON.stringify(request),
        headers: {
          'Content-Type': 'application/json',
        },
      });
    },

    // Get hybrid inference workflow status (for polling)
    getStatus: (workflowId: string): Promise<HybridInferenceStatusResponse> => {
      return this.publicFetch(`/hybrid-inference/${workflowId}/status`);
    },
  };

  // Public Analysis API (synchronous — blocks until workflow completes)
  // Note: The /public/v1 route is on the server root, not under /apiv2,
  // so we construct the URL by stripping the API path prefix.
  publicAnalysis = {
    // Analyze crash images — sends multipart form, returns full result
    analyze: (files: File[], seatingSide?: string): Promise<Record<string, unknown>> => {
      const formData = new FormData();
      for (const file of files) {
        formData.append('images', file);
      }
      if (seatingSide) {
        formData.append('seating_side', seatingSide);
      }
      // Build URL from server root (strip /apiv2 suffix from publicBaseUrl)
      const serverRoot = this.publicBaseUrl.replace(/\/apiv2\/?$/, '');
      return this.fetch(`${serverRoot}/public/v1/analyze`, {
        method: 'POST',
        body: formData,
        headers: {}, // Let browser set Content-Type with boundary for FormData
      });
    },
  };

  // IIHS Clearance Lookup API methods
  iihs = {
    // Get IIHS clearance data for a vehicle
    getClearance: (params: {
      make: string;
      model: string;
      year: number;
      vehicleType?: string;
      occupantPosition?: 'driver' | 'front_right' | 'rear';
    }): Promise<IIHSClearanceResponse> => {
      const queryParams = new URLSearchParams({
        make: params.make,
        model: params.model,
        year: params.year.toString(),
      });
      if (params.vehicleType) {
        queryParams.append('vehicle_type', params.vehicleType);
      }
      if (params.occupantPosition) {
        queryParams.append('occupant_position', params.occupantPosition);
      }
      return this.publicFetch(`/iihs-clearance?${queryParams}`);
    },
  };
}

// Create and export singleton instance
export const apiClient = new ApiClient();
